import { Target, Eye, ArrowRight, Building2, MapPin, Calendar } from 'lucide-react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';

const highlights = [
  {
    icon: Building2,
    label: 'OTEC Certificado',
  },
  {
    icon: MapPin,
    label: 'Región de Valparaíso',
  },
  {
    icon: Calendar,
    label: '+5 Años',
  },
];

function About() {
  return (
    <section id="nosotros" className="py-24 bg-white relative overflow-hidden">
      {/* Background decorations */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute -top-40 -right-40 w-80 h-80 bg-blue-50 rounded-full blur-3xl" />
        <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-cyan-50 rounded-full blur-3xl" />
      </div>

      <div className="container mx-auto px-4 relative z-10">
        <div className="grid lg:grid-cols-2 gap-16 items-center">
          {/* Left: Image */}
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
            className="relative"
          >
            {/* Decorative frame */}
            <div className="absolute -inset-4 bg-gradient-to-br from-blue-100 to-cyan-100 rounded-3xl -rotate-3" />
            <div className="absolute -inset-4 bg-gradient-to-br from-blue-500/10 to-cyan-500/10 rounded-3xl rotate-3" />

            {/* Main image */}
            <div className="relative rounded-2xl overflow-hidden shadow-2xl">
              <img
                src="/instituteroom.webp"
                alt="Instituto Valparaíso"
                className="w-full h-[400px] object-cover"
              />

              {/* Overlay gradient */}
              <div className="absolute inset-0 bg-gradient-to-t from-slate-900/60 via-transparent to-transparent" />

              {/* Floating badge */}
              <div className="absolute bottom-6 left-6 right-6">
                <div className="flex flex-wrap gap-3">
                  {highlights.map((item, index) => {
                    const Icon = item.icon;
                    return (
                      <motion.div
                        key={index}
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ delay: 0.3 + index * 0.1 }}
                        className="flex items-center gap-2 px-4 py-2 bg-white/90 backdrop-blur-sm rounded-full"
                      >
                        <Icon className="w-4 h-4 text-blue-600" />
                        <span className="text-sm font-medium text-slate-700">{item.label}</span>
                      </motion.div>
                    );
                  })}
                </div>
              </div>
            </div>
          </motion.div>

          {/* Right: Content */}
          <motion.div
            initial={{ opacity: 0, x: 50 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
          >
            <span className="inline-block px-4 py-1.5 bg-blue-100 text-blue-700 text-sm font-medium rounded-full mb-6">
              Nuestra Historia
            </span>

            <h2 className="text-3xl md:text-5xl font-bold text-slate-800 mb-6 font-heading">
              Sobre{' '}
              <span className="bg-gradient-to-r from-blue-600 to-cyan-500 bg-clip-text text-transparent">
                Nosotros
              </span>
            </h2>

            <p className="text-lg text-slate-600 leading-relaxed mb-8">
              Instituto Valparaíso es un OTEC con más de 5 años de experiencia en la educación y
              formación de personas en la región de Valparaíso. Nuestro compromiso es entregar
              capacitación de excelencia, certificada y reconocida, que permita a nuestros
              alumnos crecer profesionalmente y alcanzar sus metas.
            </p>

            {/* Mission & Vision cards */}
            <div className="grid sm:grid-cols-2 gap-4 mb-8">
              <motion.div
                whileHover={{ y: -4, scale: 1.02 }}
                className="p-6 rounded-2xl bg-gradient-to-br from-blue-50 to-cyan-50 border border-blue-100"
              >
                <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center mb-4">
                  <Target className="w-6 h-6 text-white" />
                </div>
                <h3 className="text-lg font-bold text-slate-800 mb-3">Misión</h3>
                <p className="text-sm text-slate-600 leading-relaxed">
                  Formar profesionales competentes y altamente capacitados, entregando educación de excelencia
                  a través de metodologías innovadoras y una plataforma e-learning moderna. Nos comprometemos
                  a brindar capacitación actualizada en seguridad privada, administración, contabilidad,
                  automatización y gestión logística, permitiendo a nuestros alumnos y empresas alcanzar
                  sus metas con certificaciones de reconocimiento nacional.
                </p>
              </motion.div>

              <motion.div
                whileHover={{ y: -4, scale: 1.02 }}
                className="p-6 rounded-2xl bg-gradient-to-br from-cyan-50 to-blue-50 border border-cyan-100"
              >
                <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-cyan-500 to-cyan-600 flex items-center justify-center mb-4">
                  <Eye className="w-6 h-6 text-white" />
                </div>
                <h3 className="text-lg font-bold text-slate-800 mb-3">Visión</h3>
                <p className="text-sm text-slate-600 leading-relaxed">
                  Ser el OTEC líder a nivel nacional en Chile, reconocido por nuestra excelencia académica,
                  innovación tecnológica y compromiso con la satisfacción de nuestros alumnos. Aspiramos a
                  expandir nuestra oferta formativa, formar alianzas estratégicas con empresas líderes y
                  mantenernos siempre a la vanguardia, transformando vidas a través de una educación
                  de calidad que impulse el desarrollo profesional y personal de cada estudiante.
                </p>
              </motion.div>
            </div>

            {/* CTA Button */}
            <Link to="/nosotros">
              <motion.button
                whileHover={{ scale: 1.05, y: -2 }}
                whileTap={{ scale: 0.98 }}
                className="group inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-blue-600 to-cyan-500 hover:from-blue-700 hover:to-cyan-600 text-white font-semibold rounded-xl shadow-lg shadow-blue-500/30 transition-all duration-300"
              >
                Conoce Más Sobre Nosotros
                <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
              </motion.button>
            </Link>
          </motion.div>
        </div>
      </div>
    </section>
  );
}

export default About;
